import { useQueries, useQueryClient } from '@tanstack/vue-query';
import { ApiError } from '@/api/errors';
import { fetchOrgDocuments, useOrgDocuments } from '@/modules/documents/api';
import { ORGANISATION_DOCUMENTS_QUERY } from '@/modules/documents/api/queryKeys';
import type { Document } from '@/modules/documents/types';
import { fetchClientDocument } from '@/modules/client-documents/api';
import { CLIENT_DOCUMENT_QUERY } from '@/modules/client-documents/api/queryKeys';
import type { ClientDocumentDetail } from '@/modules/client-documents/types';
import { useTargetOrgType } from '../targetOrgType';
import { useSupplierAssessmentAnswers, useSupplierInfo } from './supplier';

/** One document given as evidence on an answer, wherever the document lives. */
export type EvidenceDocument = {
  id: string;
  title: string;
  uploadID: string;
  contentType: string | null;
  // When the document was attached to this answer. Absent where nothing records it.
  attachedAt?: string;
  // The supplier document behind the evidence, which the preview's side panel reads.
  document?: Document;
};

type AnswerWithEvidence = {
  answerID: string;
  documentIDs: string[];
};

function supplierEvidence(document: Document, answerID: string): EvidenceDocument {
  return {
    id: document.id,
    title: document.name,
    uploadID: document.uploadID,
    contentType: null,
    attachedAt:
      document.attachment.find((a) => a.entityID === answerID)?.createdAt ?? document.createdAt,
    document,
  };
}

function clientEvidence(document: ClientDocumentDetail): EvidenceDocument {
  return {
    id: document.id,
    title: document.title,
    uploadID: document.uploadID,
    contentType: document.contentType,
  };
}

const isDeleted = (err: unknown) => err instanceof ApiError && err.status === 404;

// A document deleted since it was attached can't be read, so it is left out.
function resolve<TDocument>(
  answer: AnswerWithEvidence,
  documentsByID: Map<string, TDocument>,
  toEvidence: (document: TDocument) => EvidenceDocument,
) {
  return answer.documentIDs.flatMap((id) => {
    const document = documentsByID.get(id);
    return document ? [toEvidence(document)] : [];
  });
}

const byID = <TDocument extends { id: string }>(documents: TDocument[]) =>
  new Map(documents.map((document) => [document.id, document]));

/**
 * The evidence on the review's answers. A client set's evidence is the client's
 * own documents, and any other set's is the reviewed organisation's, so this is
 * the one place that knows where the documents live.
 *
 * `enabled` gates only the reads behind `evidenceFor`. `fetchEvidenceFor` reads
 * what it needs when called, so an export can leave them off.
 */
export function useAssessmentEvidence(
  orgID: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean | undefined;
    type?: MaybeRefOrGetter<'client' | 'supplier'>;
  },
) {
  const isEnabled = () => !options?.enabled || options.enabled() === true;

  // Only a supplier is reviewed through answer sets.
  const type = useTargetOrgType(options?.type);
  const { answerSet } = useSupplierInfo(orgID, { enabled: () => type.value === 'supplier' });
  const isClientSet = computed(() => answerSet.value?.kind === 'client');

  const { documents: orgDocuments, query: documentsQuery } = useOrgDocuments(orgID, {
    enabled: () => isEnabled() && !isClientSet.value,
  });
  const orgDocumentsByID = computed(() => byID(orgDocuments.value));

  // On a client set, the evidence is every client document the set's answers cite.
  const { answers } = useSupplierAssessmentAnswers(orgID, {
    enabled: () => isEnabled() && isClientSet.value,
  });
  const clientDocumentIDs = computed(() => [
    ...new Set(answers.value.flatMap((answer) => answer.documentIDs)),
  ]);
  const queryClient = useQueryClient();
  // One read per document, through the cache entry the answering view shares, so a
  // failed read hides only that document. A failure shows in the review with a
  // retry, not in the app's error handler.
  const clientDocumentQueries = useQueries({
    queries: computed(() =>
      isEnabled() && isClientSet.value
        ? clientDocumentIDs.value.map((id) => ({
            queryKey: [CLIENT_DOCUMENT_QUERY, id] as const,
            queryFn: () => fetchClientDocument(id),
            throwOnError: false,
          }))
        : [],
    ),
  });
  const clientDocumentsByID = computed(() =>
    byID(clientDocumentQueries.value.flatMap((query) => (query.data ? [query.data] : []))),
  );
  const failedClientDocumentQueries = computed(() =>
    clientDocumentQueries.value.filter((query) => query.isError && !isDeleted(query.error)),
  );

  function evidenceFor(answer: AnswerWithEvidence): EvidenceDocument[] {
    return isClientSet.value
      ? resolve(answer, clientDocumentsByID.value, clientEvidence)
      : resolve(answer, orgDocumentsByID.value, (document) =>
          supplierEvidence(document, answer.answerID),
        );
  }

  /**
   * Resolves evidence for an export, which is built once, so every document is
   * read before it returns rather than filling in as reads land.
   */
  async function fetchEvidenceFor(
    exportAnswers: AnswerWithEvidence[],
  ): Promise<(answer: AnswerWithEvidence) => EvidenceDocument[]> {
    if (isClientSet.value) {
      const ids = [...new Set(exportAnswers.flatMap((answer) => answer.documentIDs))];
      // A deleted document is left out; any other failed read fails the export
      // rather than producing one quietly missing evidence.
      const clientDocuments = byID(
        (
          await Promise.all(
            ids.map((id) =>
              queryClient
                .fetchQuery({
                  queryKey: [CLIENT_DOCUMENT_QUERY, id],
                  queryFn: () => fetchClientDocument(id),
                })
                .catch((err: unknown) => {
                  if (isDeleted(err)) return undefined;
                  throw err;
                }),
            ),
          )
        ).filter((document) => document !== undefined),
      );
      return (answer) => resolve(answer, clientDocuments, clientEvidence);
    }
    const id = toValue(orgID);
    const supplierDocuments = await queryClient.fetchQuery({
      queryKey: [ORGANISATION_DOCUMENTS_QUERY, id],
      queryFn: async () => (await fetchOrgDocuments(id)).data,
    });
    const supplierDocumentsByID = byID(supplierDocuments);
    return (answer) =>
      resolve(answer, supplierDocumentsByID, (document) =>
        supplierEvidence(document, answer.answerID),
      );
  }

  return {
    evidenceFor,
    fetchEvidenceFor,
    isLoading: computed(() =>
      isClientSet.value
        ? clientDocumentQueries.value.some((query) => query.isLoading)
        : documentsQuery.isLoading.value,
    ),
    isError: computed(() =>
      isClientSet.value
        ? failedClientDocumentQueries.value.length > 0
        : documentsQuery.isError.value,
    ),
    refetch: () =>
      isClientSet.value
        ? Promise.all(failedClientDocumentQueries.value.map((query) => query.refetch()))
        : documentsQuery.refetch(),
  };
}
